// src/Blogs/BlogTileFixing.jsx
import React, { useEffect, useRef, useState } from "react";

// Tiling-related images (inserted inline, not a gallery)
import book1 from "../assets/BlogImg/CuringCleaning&Protection.png";     // finished look
import book2 from "../assets/BlogImg/StandardTileFixing.png";    // grouting/finishing
import book3 from "../assets/BlogImg/Materials&Tools.png";       // pre-tiling preparation
import book4 from "../assets/BlogImg/AdhesiveMixing.png";       // laying process

/**
 * STANDARD TILE FIXING PROCEDURE (Floor & Wall)
 * Layout mirrors BlogSafetyTraining: progress bar, hero header, readable sections, TOC.
 * Content follows your document sections without adding extra content.
 */

export default function BlogTileFixing() {
  const sections = [
    { id: "scope", title: "Scope & Objective" },
    { id: "materials", title: "Materials & Tools" },
    { id: "prechecks", title: "Pre-Tiling Checks" },
    { id: "surface-prep", title: "Surface Preparation" },
    { id: "layout", title: "Dry Layout & Setting Out" },
    { id: "adhesive", title: "Adhesive Mixing" },
    { id: "floor", title: "Floor Tiling Procedure" },
    { id: "wall", title: "Wall Tiling Procedure" },
    { id: "cutting", title: "Cutting & Openings" },
    { id: "joints", title: "Joints, Movement & Edges" },
    { id: "grouting", title: "Grouting" },
    { id: "curing", title: "Curing, Cleaning & Protection" },
    { id: "quality", title: "Quality Checks" },
    { id: "issues", title: "Common Issues to Avoid" },
    { id: "safety", title: "Safety" },
  ];

  // Reading progress
  const [progress, setProgress] = useState(0);
  const articleRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const done = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? Math.round((done / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#F7FAFF] via-[#F3F6FB] to-[#F9FBFF]">
      {/* Progress bar */}
      <div className="sticky top-0 z-40 h-1 bg-transparent">
        <div
          className="h-1 bg-gradient-to-r from-sky-500 via-cyan-500 to-indigo-500 transition-[width] duration-150 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
        <div className="relative rounded-[28px] overflow-hidden">
          <div className="absolute -inset-[2px] rounded-[30px] bg-gradient-to-r from-sky-300/50 via-cyan-300/50 to-indigo-400/50 blur-md" />
          <div className="relative rounded-[28px] bg-white/95 ring-1 ring-white/70 backdrop-blur-xl shadow-[0_30px_80px_rgba(2,6,23,.12)] p-6 sm:p-9">
            <p className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-sky-800/90 font-semibold">
              Construction • Tiling • Procedure
            </p>
            <h1 className="mt-2 text-[26px] sm:text-[34px] lg:text-[38px] font-black leading-tight">
              <span className="bg-gradient-to-r from-sky-700 via-cyan-700 to-indigo-800 bg-clip-text text-transparent">
                STANDARD TILE FIXING PROCEDURE (Floor &amp; Wall)
              </span>
            </h1>
          </div>
        </div>
      </section>

      {/* Body + TOC */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-12">
        {/* Article */}
        <article ref={articleRef} className="max-w-3xl w-full justify-self-center lg:justify-self-auto">
          {/* 1. Scope & Objective */}
          <Section id="scope" title="Scope & Objective">
            <p>
              Defines the standard method for fixing ceramic, vitrified, porcelain, or stone tiles on floors and
              walls—from surface preparation and layout to adhesive application, laying, grouting, curing, and finishing.
            </p>
          </Section>

          <InlineImage src={book3} alt="Pre-tiling preparation" />

          {/* 2. Materials & Tools */}
          <Section id="materials" title="Materials & Tools">
            <List
              items={[
                "Tiles as per drawing/approved sample (batch/shade verified).",
                "Polymer-modified tile adhesive (grade as specified for floor/wall/stone).",
                "Grout (cementitious/epoxy) as specified; approved/matching shade.",
                "Primers/latex (if specified), spacers, movement-joint profiles.",
                "Tools: notched trowel, rubber mallet, spirit level, straightedge, tile cutter, wedges, sponges, buckets, mixing paddle.",
                "PPE: gloves, goggles, masks, safety shoes.",
              ]}
            />
          </Section>

          {/* 3. Pre-Tiling Checks */}
          <Section id="prechecks" title="Pre-Tiling Checks">
            <List
              items={[
                "Confirm substrate completion, curing, and load transfer readiness.",
                "Verify line, level, plane, and slope per drawings (especially in wet areas).",
                "Ensure substrate is sound, crack-free (or cracks treated), clean, and dry.",
                "Remove laitance, dust, oil, paint, curing compounds, and loose particles.",
                "Check plumb of walls; rectify deviations beyond tolerance.",
                "Confirm layout, pattern, cut locations, and movement joints with drawings.",
              ]}
            />
          </Section>

          {/* 4. Surface Preparation */}
          <Section id="surface-prep" title="Surface Preparation">
            <List
              items={[
                "Grinding/roughening where required to improve adhesion.",
                "Repairs/patching with approved mortar; allow to set/cure.",
                "Priming/latex bonding as specified by adhesive manufacturer.",
                "Lightly pre-wet porous substrates if recommended (no standing water).",
              ]}
            />
          </Section>

          {/* 5. Dry Layout & Setting Out */}
          <Section id="layout" title="Dry Layout & Setting Out">
            <List
              items={[
                "Snap control lines to establish square reference and centerlines.",
                "Dry-lay tiles to confirm joint widths and minimize narrow cuts at edges.",
                "Confirm pattern (straight, staggered/brick, diagonal) and orientation.",
                "Use control/guide battens at datum lines where useful (for walls).",
              ]}
            />
          </Section>

          <InlineImage src={book4} alt="Tiling laying process" />

          {/* 6. Adhesive Mixing */}
          <Section id="adhesive" title="Adhesive Mixing">
            <List
              items={[
                "Mix with clean water per manufacturer’s ratio using a slow-speed mixer.",
                "Allow slake/rest if specified; remix. Do not add water after pot life.",
                "Prepare small batches to use within open time; discard skin-formed adhesive.",
              ]}
            />
          </Section>

          {/* 7. Floor Tiling Procedure */}
          <Section id="floor" title="Floor Tiling Procedure">
            <List
              items={[
                "Spread adhesive with a notched trowel (comb in one direction) within open time.",
                "Back-butter large/stone tiles for full contact where required.",
                "Place tile, slide slightly, and tap with rubber mallet; maintain joint width with spacers.",
                "Check line, level, and lippage with straightedge/level during laying.",
                "Lift and verify coverage when starting; target near full-bed support.",
                "Maintain slopes toward drains in wet areas; keep weep holes unobstructed.",
              ]}
            />
          </Section>

          {/* 8. Wall Tiling Procedure */}
          <Section id="wall" title="Wall Tiling Procedure">
            <List
              items={[
                "Start from a level datum batten; work upwards in rows.",
                "Apply adhesive on wall; back-butter for large/low-absorption tiles.",
                "Use spacers/wedges; check plumb and alignment regularly.",
                "Support heavier tiles mechanically if required until set.",
              ]}
            />
          </Section>

          {/* 9. Cutting & Openings */}
          <Section id="cutting" title="Cutting & Openings">
            <List
              items={[
                "Cut with appropriate cutter/wet saw; smooth sharp edges.",
                "Form neat cutouts for pipes, electrical boxes, and fixtures.",
                "Avoid sliver cuts; adjust layout to keep balanced margins.",
              ]}
            />
          </Section>

          {/* 10. Joints, Movement & Edges */}
          <Section id="joints" title="Joints, Movement & Edges">
            <List
              items={[
                "Maintain uniform grout joints as specified (use spacers).",
                "Provide movement/expansion joints per drawings and at transitions; fill with sealant or profiles.",
                "Use trims/profiles on external corners/edges where required.",
              ]}
            />
          </Section>

          <InlineImage src={book2} alt="Grouting and finishing" />

          {/* 11. Grouting */}
          <Section id="grouting" title="Grouting">
            <List
              items={[
                "Grout only after adhesive has set per manufacturer’s time.",
                "Mix grout per data sheet; fill joints diagonally with rubber float.",
                "Clean excess with damp sponge as it firms up; avoid washing out joints.",
                "For epoxy grout, follow pot life and cleaning windows strictly.",
              ]}
            />
          </Section>

          {/* 12. Curing, Cleaning & Protection */}
          <Section id="curing" title="Curing, Cleaning & Protection">
            <List
              items={[
                "Protect freshly laid tiles from traffic until cured.",
                "Final clean with approved cleaners; avoid acids unless specified.",
                "Cover with protective sheets during subsequent works.",
              ]}
            />
          </Section>

          {/* 13. Quality Checks */}
          <Section id="quality" title="Quality Checks">
            <List
              items={[
                "Line, level, plane, and slopes as per drawings.",
                "Uniform joint width; no hollow sounds on tapping.",
                "No lippage beyond tolerance; clean surface without stains.",
                "Movement joints present and correctly detailed.",
              ]}
            />
          </Section>

          {/* 14. Common Issues to Avoid */}
          <Section id="issues" title="Common Issues to Avoid">
            <List
              items={[
                "Laying over dusty/uneven or damp substrates without prep.",
                "Using expired adhesive/grout or exceeding open/pot life.",
                "Insufficient coverage causing hollow/loose tiles.",
                "Skipping movement joints; improper cleaning of grout haze.",
              ]}
            />
          </Section>

          <InlineImage src={book1} alt="Completed floor and wall" />

          {/* 15. Safety */}
          <Section id="safety" title="Safety" last>
            <List
              items={[
                "Wear PPE (gloves, goggles, masks); ensure ventilation during mixing/cleaning.",
                "Handle tools/cutters safely; keep work area tidy and dry.",
                "Dispose of waste per site/environmental requirements.",
              ]}
            />
          </Section>

          <div className="mt-8 flex justify-end">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="text-sm px-3 py-2 rounded-full bg-white text-slate-700 ring-1 ring-slate-200 shadow-sm hover:bg-slate-50"
            >
              ↑ Back to top
            </button>
          </div>
        </article>

        {/* TOC (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl bg-white/95 ring-1 ring-slate-200/70 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-600 mb-2">On this page</p>
            <nav className="space-y-1.5 text-[13px] leading-5">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block px-2 py-1 rounded hover:bg-slate-50 text-slate-700 hover:text-sky-700"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </main>
  );
}

/* ---------- Presentational helpers (same as Safety blog) ---------- */

function Section({ id, title, children, last = false }) {
  return (
    <section
      id={id}
      className={`scroll-mt-28 mb-6 sm:mb-8 ${last ? "" : "border-b border-slate-200/70 pb-6 sm:pb-8"}`}
    >
      <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 p-5 sm:p-6">
        <h2 className="text-[20px] sm:text-[22px] font-black tracking-tight mb-2">
          <span className="bg-gradient-to-r from-sky-600 via-cyan-600 to-indigo-700 bg-clip-text text-transparent">
            {title}
          </span>
        </h2>
        <div className="text-[15.5px] sm:text-[16px] leading-[1.85] text-slate-800/90 space-y-3">
          {children}
        </div>
      </div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="mt-1.5 list-none space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function InlineImage({ src, alt }) {
  return (
    <figure className="my-5 sm:my-6">
      <div className="rounded-2xl overflow-hidden bg-white ring-1 ring-slate-200 shadow-sm">
        <img
          src={src}
          alt={alt}
          className="w-full h-48 sm:h-56 md:h-64 object-cover"
          loading="lazy"
          draggable={false}
        />
      </div>
    </figure>
  );
}
